<script setup lang="ts">
import { computed } from 'vue'

import { DateTime } from 'luxon'

import { ShiftOfferStatus } from '../../employee_shift/models/employeeShiftOffer'
import { RequestRound } from '../../shifts/models/requestQueue'

import { isLive, preview, tally, timing, who } from '../utils/describe'

const { requestRound, now } = defineProps<{
  requestRound: RequestRound
  now: DateTime
}>()

const live = computed(() => isLive(requestRound))
const when = computed(() => timing(requestRound, now))
const note = computed(() => preview(requestRound))

const chip: Record<ShiftOfferStatus, string> = {
  accepted: 'border-oq-live bg-oq-live-soft text-oq-live',
  applied: 'border-oq-live bg-oq-live-soft text-oq-live',
  pending: 'border-oq-line-strong text-oq-ink',
  declined: 'border-oq-line text-oq-ink-3 line-through',
  rejected: 'border-oq-line text-oq-ink-3',
  queued: 'border-oq-line text-oq-ink-3',
}
const label: Record<ShiftOfferStatus, string> = {
  accepted: 'accepted',
  pending: 'awaiting reply',
  declined: 'declined',
  applied: 'applied',
  rejected: 'rejected',
  queued: 'queued',
}

const tone = {
  done: 'text-status-success',
  next: 'text-primary',
  later: 'text-on-surface-muted',
  off: 'text-status-warn',
} as const
const off = computed(
  () =>
    requestRound.status === 'cancelled' || requestRound.status === 'skipped',
)
</script>

<template>
  <article
    class="rounded-[10px] border border-accent-400/80 bg-oq-surface"
    :class="[
      live ? 'border-oq-live/40' : 'border-oq-line',
      off && 'opacity-70',
      !!requestRound.firedAt ? 'border-solid' : 'border-2 border-dotted',
    ]"
    :aria-label="`Round ${requestRound.position}`"
  >
    <header
      class="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2"
      :class="live && 'rounded-t-[9px] bg-oq-live-soft'"
    >
      <h4 class="text-sm font-semibold text-oq-ink">
        Round {{ requestRound.position }}
      </h4>
      <span
        v-if="live"
        class="inline-flex items-center gap-1 text-[13px] font-medium text-oq-live"
      >
        Grouped
      </span>
      <span
        class="ml-auto inline-flex items-center gap-1 text-[13px] font-medium"
        :class="tone[when.tone]"
      >
        <OqIcon :name="requestRound.status === 'fired' ? 'send' : 'clock'" />
        {{ when.text }}
      </span>
    </header>

    <div class="space-y-2 px-3 pb-3 pt-2 text-sm text-oq-ink">
      <p>
        <template v-if="requestRound.status === 'fired'">Offered to </template>
        <template v-else-if="requestRound.status === 'pending' && live"
          >Sends to
        </template>
        <template v-else-if="requestRound.status === 'pending'">For </template>
        <template v-else>Was for </template>
        <span :class="live && 'font-medium'">{{ who(requestRound) }}</span
        ><template v-if="live && requestRound.status === 'pending'">
          when it sends</template
        >.
      </p>

      <template v-if="requestRound.status === 'fired'">
        <p class="text-[13px] text-oq-ink-2">
          {{ tally(requestRound.offers) }}
        </p>
        <ul class="flex flex-wrap gap-1.5" aria-label="Offers">
          <li
            v-for="offer in requestRound.offers"
            :key="offer.id"
            class="rounded-full border px-2.5 py-0.5 text-[13px]"
            :class="chip[offer.status]"
          >
            {{ offer.employee.name
            }}<span class="sr-only">, {{ label[offer.status] }}</span>
          </li>
        </ul>
      </template>

      <p v-else-if="note" class="text-[13px] text-oq-ink-2">{{ note }}</p>
    </div>
  </article>
</template>
